/**
 * Mapping mellom frontend-layoutmodellen (WarehouseLayout i types/warehouseLayout.ts)
 * og server-DTOene i warehouseLayoutService. Holder konverteringen på ett sted slik
 * at resten av editoren er uendret fra localStorage-tiden.
 */

import {
  WarehouseLayout,
  WarehouseObject,
  WarehouseObjectType,
  RackDimensions,
} from '../types/warehouseLayout';
import {
  FullServerLayout,
  ServerBinPosition,
  ServerLayoutObject,
  ServerRackGeometry,
  ServerRackPosition,
  ServerSpecialBin,
  UpsertLayoutRequest,
} from '../../../../../services/warehouseLayoutService';

// Ramme-påslag så datadrevne mål matcher heuristikkens visuelle proporsjoner
const RACK_FRAME_ALLOWANCE = 0.16; // meter (2 × 0.08 ramme)

const OBJECT_TYPE_TO_SERVER: Record<WarehouseObjectType, string> = {
  pillar: 'PILLAR',
  door: 'DOOR',
  station: 'STATION',
  charging: 'CHARGING',
  zone_marker: 'ZONE_MARKER',
};

const OBJECT_TYPE_FROM_SERVER: Record<string, WarehouseObjectType> = {
  PILLAR: 'pillar',
  DOOR: 'door',
  STATION: 'station',
  CHARGING: 'charging',
  ZONE_MARKER: 'zone_marker',
};

function toRotation(value: number): 0 | 90 | 180 | 270 {
  const normalized = ((value % 360) + 360) % 360;
  const snapped = Math.round(normalized / 90) * 90;
  return (snapped === 360 ? 0 : snapped) as 0 | 90 | 180 | 270;
}

/** Server-layout -> frontend-modellen editoren jobber med. */
export function serverToFrontendLayout(
  warehouseId: string,
  server: FullServerLayout,
  fallbackName: string
): WarehouseLayout | null {
  if (!server.layout) return null;

  return {
    warehouseId,
    name: server.layout.layoutName || fallbackName,
    dimensions: {
      length: server.layout.lengthMeters,
      width: server.layout.widthMeters,
      height: server.layout.heightMeters,
    },
    gridUnit: server.layout.gridUnitMeters || 0.5,
    racks: server.rackPositions.map(p => ({
      rackNumber: p.rackNumber,
      x: p.positionX,
      y: p.positionY,
      rotation: toRotation(p.rotation),
      label: p.rackLabel || undefined,
      color: p.color || undefined,
    })),
    objects: server.objects.map(serverObjectToFrontend),
    version: 1,
    lastModified: server.layout.lastUpdate || new Date().toISOString(),
  };
}

function serverObjectToFrontend(obj: ServerLayoutObject): WarehouseObject {
  return {
    id: `srv-${obj.id}`,
    type: OBJECT_TYPE_FROM_SERVER[obj.objectType] || 'pillar',
    name: obj.objectName || '',
    x: obj.positionX,
    y: obj.positionY,
    width: obj.width,
    depth: obj.depth,
    height: obj.height ?? undefined,
    rotation: obj.rotation,
    color: obj.color || undefined,
  };
}

/** Frontend-layout -> requests for lagring. expectedLastUpdate aktiverer 409-vernet. */
export function frontendToUpsertLayoutRequest(
  layout: WarehouseLayout,
  expectedLastUpdate?: string | null
): UpsertLayoutRequest {
  return {
    layoutName: layout.name,
    lengthMeters: layout.dimensions.length,
    widthMeters: layout.dimensions.width,
    heightMeters: layout.dimensions.height,
    gridUnitMeters: layout.gridUnit,
    expectedLastUpdate: expectedLastUpdate ?? null,
  };
}

export function frontendToRackPositions(layout: WarehouseLayout): ServerRackPosition[] {
  return layout.racks.map(r => ({
    rackNumber: r.rackNumber,
    levelNo: 0,
    positionX: r.x,
    positionY: r.y,
    rotation: r.rotation,
    rackLabel: r.label ?? null,
    color: r.color ?? null,
  }));
}

export function frontendToServerObjects(layout: WarehouseLayout): Omit<ServerLayoutObject, 'id'>[] {
  return layout.objects.map(o => ({
    levelNo: 0,
    objectType: OBJECT_TYPE_TO_SERVER[o.type] || 'PILLAR',
    objectName: o.name || null,
    positionX: o.x,
    positionY: o.y,
    width: o.width,
    depth: o.depth,
    height: o.height ?? null,
    rotation: o.rotation ?? 0,
    color: o.color ?? null,
    stcZoneID: null,
  }));
}

// ----------------------------------------------------------------------------
// Enkeltlokasjoner (gulv/vegg/heng/skap/dock) — render-info for 2D/3D
// ----------------------------------------------------------------------------

export interface PlacedBinRenderInfo {
  binCode: string;
  description?: string;
  kind: ServerBinPosition['placementKind'];
  x: number;
  y: number;
  rotation: number;
  widthM: number;
  depthM: number;
  heightM: number;
  color: string;
}

const KIND_DEFAULTS: Record<string, { w: number; d: number; h: number; color: string }> = {
  FLOOR:   { w: 1.2, d: 1.2, h: 1.0, color: '#0ea5e9' },
  WALL:    { w: 1.0, d: 0.4, h: 2.0, color: '#a78bfa' },
  HANGING: { w: 1.5, d: 0.5, h: 1.8, color: '#f472b6' },
  CABINET: { w: 1.0, d: 0.6, h: 2.0, color: '#94a3b8' },
  DOCK:    { w: 2.5, d: 1.5, h: 0.3, color: '#ef4444' },
};

/**
 * Slår sammen posisjon (BinPosition), mål fra StcBinSize (SpecialBin) og
 * kind-defaults til ferdig render-info. Overrides vinner alltid.
 */
export function resolveBinRenderInfo(
  position: ServerBinPosition,
  specialBinsByCode: Map<string, ServerSpecialBin>
): PlacedBinRenderInfo {
  const defaults = KIND_DEFAULTS[position.placementKind] || KIND_DEFAULTS.FLOOR;
  const special = specialBinsByCode.get(position.binCode);

  return {
    binCode: position.binCode,
    description: special?.description1 || undefined,
    kind: position.placementKind,
    x: position.positionX,
    y: position.positionY,
    rotation: position.rotation,
    widthM: position.widthOverride ?? special?.widthMeters ?? defaults.w,
    depthM: position.depthOverride ?? special?.depthMeters ?? defaults.d,
    heightM: position.heightOverride ?? special?.heightMeters ?? defaults.h,
    color: position.color || defaults.color,
  };
}

/** Standard visningsfarge per objekttype (matcher 3D-scenen). */
export const OBJECT_TYPE_COLORS: Record<WarehouseObjectType, string> = {
  pillar: '#6b7280',
  door: '#f59e0b',
  station: '#3b82f6',
  charging: '#10b981',
  zone_marker: '#8b5cf6',
};

/**
 * Datadreven server-geometri -> RackDimensions for editoren.
 * Returnerer null når serveren ikke hadde brukbar StcBinSize-dekning for reolen
 * (kalleren faller da tilbake til calculateRackDimensions-heuristikken).
 */
export function serverGeometryToRackDimensions(geometry: ServerRackGeometry): RackDimensions | null {
  if (!geometry.hasBinSizeData) return null;
  if (geometry.widthMeters <= 0 || geometry.depthMeters <= 0 || geometry.heightMeters <= 0) return null;

  return {
    width: geometry.widthMeters + RACK_FRAME_ALLOWANCE,
    depth: geometry.depthMeters + RACK_FRAME_ALLOWANCE,
    height: geometry.heightMeters,
    verticals: Math.max(1, geometry.verticals.length),
    shelves: Math.max(1, geometry.shelves.length),
    drawers: undefined,
  };
}
